import React, { useState, useEffect, useCallback } from 'react';
import { BookOpen, GraduationCap, Users, Target, Award, Brain, Zap, Loader, Settings, BarChart3, FileText, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';
import PrimaryDrillQuestions from './PrimaryDrillQuestions';
import DrillProgress from './DrillProgress';
import ExamPaperGenerator from './PriTools/ExamPaperGenerator';
import primaryMathsDataService from '../services/primaryMathsDataService';
import { clearDrillResults } from '../data/drillData';

const PrimaryMathsContainer = () => {
  const [activeSubTab, setActiveSubTab] = useState('p1');
  const [activePriToolsTab, setActivePriToolsTab] = useState('generator');
  const [showExamPaperGenerator, setShowExamPaperGenerator] = useState(false);
  const [drillDataMap, setDrillDataMap] = useState({});
  const [isGeneratingDrill, setIsGeneratingDrill] = useState(false);
  const [progressData, setProgressData] = useState(null);
  const [generatingQuestionId, setGeneratingQuestionId] = useState(null);
  const [drillProgress, setDrillProgress] = useState({});
  
  // State for collapse/expand sections
  const [expandedSections, setExpandedSections] = useState({
    // Format: 'topicId-sectionType' => true/false
    // e.g., 'p1-1n1-objectives' => true
  });
  
  // State for storing fetched data
  const [primaryData, setPrimaryData] = useState({
    p1: null,
    p2: null,
    p3: null,
    p4: null,
    p5: null,
    p6: null
  });
  const [loadingStates, setLoadingStates] = useState({
    p1: false,
    p2: false,
    p3: false,
    p4: false,
    p5: false,
    p6: false
  });

  // Fetch data for a specific grade using A2A
  const fetchGradeData = useCallback(async (grade, forceRefresh = false) => {
    const key = grade.toLowerCase();
    setLoadingStates(prev => ({ ...prev, [key]: true }));
    
    try {
      const data = await primaryMathsDataService.getGradeData(grade, forceRefresh);
      if (data) {
        setPrimaryData(prev => ({ ...prev, [key]: data }));
      } else {
        console.warn(`No data returned for grade ${grade} from A2A service`);
      }
    } catch (error) {
      console.error(`Error loading ${grade} data from A2A service:`, error);
      console.error('Error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint
      });
    } finally {
      setLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  }, []);

  // Fetch data for all grades on component mount using A2A
  useEffect(() => {
    const initializeData = async () => {
      // Clear all cache first to ensure fresh data
      primaryMathsDataService.clearCache();
      console.log('Cache cleared, fetching fresh data via A2A...');

      // Fetch all grades - force refresh to get latest data
      const grades = ['P1', 'P2', 'P3', 'P4', 'P5', 'P6'];
      
      for (const grade of grades) {
        await fetchGradeData(grade, true); // Force refresh on initial load
      }
    };

    initializeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh data when tab changes (force refresh to get latest data)
  useEffect(() => {
    const gradeMap = {
      'p1': 'P1',
      'p2': 'P2',
      'p3': 'P3',
      'p4': 'P4',
      'p5': 'P5',
      'p6': 'P6'
    };

    if (gradeMap[activeSubTab] && activeSubTab !== 'pritools') {
      // Always force refresh when switching tabs to ensure latest data from Supabase
      fetchGradeData(gradeMap[activeSubTab], true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSubTab]);

  // Handle manual refresh
  const handleRefresh = async () => {
    const gradeMap = {
      'p1': 'P1',
      'p2': 'P2',
      'p3': 'P3',
      'p4': 'P4',
      'p5': 'P5',
      'p6': 'P6'
    };

    if (gradeMap[activeSubTab] && activeSubTab !== 'pritools') {
      await fetchGradeData(gradeMap[activeSubTab], true);
    }
  };

  const handleGenerateDrill = async (question, isRegeneration = false) => {
    if (isGeneratingDrill) return;
    
    setIsGeneratingDrill(true);
    setProgressData(null);
    setGeneratingQuestionId(question.id);
    
    // Initialize progress tracking for this question
    setDrillProgress(prev => ({
      ...prev,
      [question.id]: {
        isGenerating: true,
        startTime: Date.now(),
        tokensUsed: 0,
        estimatedTokens: 0,
        costIncurred: 0,
        stage: 'preparing',
        message: isRegeneration ? '準備生成全新小學練習題...' : '準備生成小學練習題...'
      }
    }));
    
    try {
      // Get current grade from activeSubTab
      const gradeMap = {
        'p1': 'P1',
        'p2': 'P2',
        'p3': 'P3',
        'p4': 'P4',
        'p5': 'P5',
        'p6': 'P6'
      };
      const currentGrade = gradeMap[activeSubTab] || 'P1';
      
      // Add variation parameters to ensure different questions on regeneration
      const variationParams = isRegeneration ? {
        variationSeed: Date.now(), // Use timestamp as seed for variation
        requestVariation: true, // Explicit flag to request different questions
        avoidRepetition: true // Ask AI to avoid repeating previous questions
      } : {};
      
      const drillResult = await primaryMathsDataService.generateDrillQuestions(
        question,
        currentGrade,
        {
          numberOfDrills: 5,
          topicId: question.topicId || null,
          ...variationParams, // Include variation parameters
          onProgress: (progress) => {
            setProgressData(progress);
            // Update progress tracking
            setDrillProgress(prev => ({
              ...prev,
              [question.id]: {
                ...prev[question.id],
                ...progress,
                isGenerating: progress.stage !== 'complete'
              }
            }));
          }
        }
      );
      
      // Store drill data for this specific question
      setDrillDataMap(prev => ({
        ...prev,
        [question.id]: drillResult
      }));
      setProgressData(null);
    } catch (error) {
      console.error('Error generating drill:', error);
      alert('生成練習題時發生錯誤：' + error.message);
    } finally {
      setIsGeneratingDrill(false);
      setGeneratingQuestionId(null);
      // Clear progress tracking
      setDrillProgress(prev => ({
        ...prev,
        [question.id]: {
          ...prev[question.id],
          isGenerating: false
        }
      }));
    }
  };

  const handleDrillComplete = (results) => {
    console.log('Drill completed:', results);
    // You can add additional logic here for drill completion
  };

  const handleGenerateNewDrill = async (questionId) => {
    // Clear existing drill data and results for this question
    setDrillDataMap(prev => {
      const newMap = { ...prev };
      delete newMap[questionId];
      return newMap;
    });
    
    // Clear drill results from both in-memory storage and localStorage
    try {
      clearDrillResults(questionId);
      console.log(`[PrimaryMathsContainer] Cleared all drill results for question ${questionId}`);
    } catch (error) {
      console.warn('Failed to clear drill results:', error);
    }
    
    // Find the question by ID and regenerate drill
    const currentData = primaryData[activeSubTab];
    if (currentData) {
      for (const topic of currentData.topics) {
        for (const question of topic.sampleQuestions) {
          if (question.id === questionId) {
            // Add variation parameter to ensure different questions
            await handleGenerateDrill(question, true);
            return;
          }
        }
      }
    }
  };

  const toggleSection = (sectionKey) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  const isSectionExpanded = (sectionKey, defaultValue = false) => {
    return expandedSections[sectionKey] !== undefined 
      ? expandedSections[sectionKey] 
      : defaultValue;
  };

  const expandAllSections = (yearData) => {
    if (!yearData || !yearData.topics) return;
    
    const sections = {};
    
    // Expand all sections for all topics
    yearData.topics.forEach((topic) => {
      sections[`${topic.id}-objectives`] = true;
      sections[`${topic.id}-questions`] = true;
      
      // Add all solution sections for each question in this topic
      topic.sampleQuestions.forEach((question) => {
        sections[`${question.id}-solution`] = true;
      });
    });
    
    setExpandedSections(prev => ({
      ...prev,
      ...sections
    }));
  };

  const collapseAllSections = (yearData) => {
    if (!yearData || !yearData.topics) return;
    
    const sections = {};
    
    // Collapse all sections for all topics
    yearData.topics.forEach((topic) => {
      sections[`${topic.id}-objectives`] = false;
      sections[`${topic.id}-questions`] = false;
      
      // Collapse all solution sections for each question in this topic
      topic.sampleQuestions.forEach((question) => {
        sections[`${question.id}-solution`] = false;
      });
    });
    
    setExpandedSections(prev => ({
      ...prev,
      ...sections
    }));
  };

  const renderTopicContent = (topic) => {
    const objectivesKey = `${topic.id}-objectives`;
    const questionsKey = `${topic.id}-questions`;
    const isObjectivesExpanded = isSectionExpanded(objectivesKey, true);
    const isQuestionsExpanded = isSectionExpanded(questionsKey, true);

    return (
      <div key={topic.id} className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">{topic.title}</h3>
        <p className="text-gray-600 mb-4">{topic.description}</p>
        
        {/* Objectives */}
        <div className="mb-6">
          <button
            onClick={() => toggleSection(objectivesKey)}
            className="flex items-center justify-between w-full text-left mb-3 group"
          >
            <h4 className="text-lg font-medium text-gray-700">學習目的：</h4>
            {isObjectivesExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
            )}
          </button>
          {isObjectivesExpanded && (
            <ul className="list-disc list-inside space-y-2 text-gray-600 transition-all duration-200 ease-in-out">
              {topic.objectives.map((objective, index) => (
                <li key={index} className="text-sm">{objective}</li>
              ))}
            </ul>
          )}
        </div>

        {/* Sample Questions */}
        <div>
          <button
            onClick={() => toggleSection(questionsKey)}
            className="flex items-center justify-between w-full text-left mb-3 group"
          >
            <h4 className="text-lg font-medium text-gray-700">示例題目：</h4>
            {isQuestionsExpanded ? (
              <ChevronUp className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
            ) : (
              <ChevronDown className="w-5 h-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
            )}
          </button>
          {isQuestionsExpanded && (
            <div className="space-y-4 transition-all duration-200 ease-in-out">
              {topic.sampleQuestions.map((question) => {
                const solutionKey = `${question.id}-solution`;
                const isSolutionExpanded = isSectionExpanded(solutionKey, true);
                
                return (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          {question.difficulty === 'basic' ? '基礎' : 
                           question.difficulty === 'intermediate' ? '中級' : '高級'}
                        </span>
                        <button
                          onClick={() => handleGenerateDrill(question)}
                          disabled={isGeneratingDrill}
                          className={`flex items-center space-x-2 px-3 py-1 rounded-lg text-sm transition-colors ${
                            isGeneratingDrill && generatingQuestionId === question.id
                              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                              : 'bg-green-500 text-white hover:bg-green-600'
                          }`}
                        >
                          {isGeneratingDrill && generatingQuestionId === question.id ? (
                            <>
                              <Loader className="w-4 h-4 animate-spin" />
                              <span>生成中...</span>
                            </>
                          ) : (
                            <>
                              <Zap className="w-4 h-4" />
                              <span>AI Drill</span>
                            </>
                          )}
                        </button>
                      </div>
                      <p className="text-gray-800 font-medium">{question.question}</p>
                    </div>
                    
                    <div className="bg-gray-50 rounded-lg p-3 mb-3">
                      <p className="text-sm text-gray-600 mb-1">
                        <span className="font-medium">答案：</span>
                        <span className="text-green-600 font-medium">{question.answer}</span>
                      </p>
                    </div>
                    
                    <div className="bg-blue-50 rounded-lg p-3">
                      <button
                        onClick={() => toggleSection(solutionKey)}
                        className="flex items-center justify-between w-full text-left mb-1 group"
                      >
                        <p className="text-sm text-gray-600 font-medium">解題步驟：</p>
                        {isSolutionExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-500 group-hover:text-gray-700 transition-colors" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500 group-hover:text-gray-700 transition-colors" />
                        )}
                      </button>
                      {isSolutionExpanded && (
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap transition-all duration-200 ease-in-out">
                          {question.solution}
                        </pre>
                      )}
                    </div>

                    {/* AI Drill Section */}
                    {drillDataMap[question.id] && (
                      <div className="mt-6 border-t pt-6">
                        <PrimaryDrillQuestions
                          drillData={drillDataMap[question.id]}
                          originalQuestionId={question.id}
                          onDrillComplete={handleDrillComplete}
                          onGenerateNewDrill={() => handleGenerateNewDrill(question.id)}
                          progressData={generatingQuestionId === question.id ? progressData : null}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPrimaryYearContent = (yearData, loading) => {
    if (loading) {
      return (
        <div className="text-center py-8">
          <Loader className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
          <div className="text-gray-500">載入中...</div>
        </div>
      );
    }

    if (!yearData) {
      return (
        <div className="text-center py-8">
          <div className="text-red-500">無法載入資料，請稍後再試。</div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-4 mb-4">
            <h2 className="text-3xl font-bold text-gray-800">{yearData.title}</h2>
            <button
              onClick={handleRefresh}
              className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              title="重新載入資料 (Refresh Data)"
            >
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
          <div className="flex items-center justify-center gap-4 mb-4">
            <p className="text-gray-600 text-lg">{yearData.description}</p>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => expandAllSections(yearData)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 hover:bg-blue-100 rounded-lg transition-colors border border-blue-200"
                title="展開全部 (Expand All)"
              >
                <ChevronDown className="w-4 h-4" />
                <span>展開全部</span>
              </button>
              <button
                onClick={() => collapseAllSections(yearData)}
                className="flex items-center space-x-1 px-3 py-1.5 text-sm bg-gray-50 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                title="收起全部 (Collapse All)"
              >
                <ChevronUp className="w-4 h-4" />
                <span>收起全部</span>
              </button>
            </div>
          </div>
        </div>

        {/* Topics */}
        <div className="space-y-6">
          {yearData.topics.map(renderTopicContent)}
        </div>
      </div>
    );
  };

  // Generator Page Component
  const GeneratorPage = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Primary Tools Generator</h2>
          <p className="text-gray-600 text-lg">Generate customized learning materials and exercises for primary students</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <FileText className="w-8 h-8 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Worksheet Generator</h3>
            </div>
            <p className="text-gray-600 mb-4">Create customized worksheets based on specific topics and difficulty levels</p>
            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
              Generate Worksheet
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Brain className="w-8 h-8 text-green-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Quiz Generator</h3>
            </div>
            <p className="text-gray-600 mb-4">Generate interactive quizzes with multiple choice and short answer questions</p>
            <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">
              Generate Quiz
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Zap className="w-8 h-8 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Flashcard Generator</h3>
            </div>
            <p className="text-gray-600 mb-4">Create digital flashcards for vocabulary and concept learning</p>
            <button className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors">
              Generate Flashcards
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Target className="w-8 h-8 text-orange-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Practice Test Generator</h3>
            </div>
            <p className="text-gray-600 mb-4">Generate comprehensive practice tests with detailed solutions</p>
            <button 
              onClick={() => setShowExamPaperGenerator(true)}
              className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors"
            >
              Generate Practice Test
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Assessment Page Component
  const AssessmentPage = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Assessment Tools</h2>
          <p className="text-gray-600 text-lg">Comprehensive assessment tools for evaluating student progress</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Progress Assessment</h3>
            </div>
            <p className="text-gray-600 mb-4">Track student progress across different topics and skills</p>
            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
              View Progress
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Award className="w-8 h-8 text-green-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Skill Assessment</h3>
            </div>
            <p className="text-gray-600 mb-4">Assess specific mathematical skills and competencies</p>
            <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">
              Start Assessment
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Users className="w-8 h-8 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Class Assessment</h3>
            </div>
            <p className="text-gray-600 mb-4">Evaluate overall class performance and identify areas for improvement</p>
            <button className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors">
              Class Report
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Target className="w-8 h-8 text-orange-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Diagnostic Assessment</h3>
            </div>
            <p className="text-gray-600 mb-4">Identify learning gaps and provide targeted recommendations</p>
            <button className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors">
              Run Diagnostic
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Performance Report Page Component
  const PerformanceReportPage = () => {
    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">Performance Reports</h2>
          <p className="text-gray-600 text-lg">Detailed performance analytics and reporting tools</p>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <BarChart3 className="w-8 h-8 text-blue-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Individual Reports</h3>
            </div>
            <p className="text-gray-600 mb-4">Generate detailed performance reports for individual students</p>
            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors">
              Generate Report
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Users className="w-8 h-8 text-green-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Class Reports</h3>
            </div>
            <p className="text-gray-600 mb-4">Comprehensive class performance analysis and insights</p>
            <button className="w-full bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors">
              View Class Report
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <Settings className="w-8 h-8 text-purple-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Analytics Dashboard</h3>
            </div>
            <p className="text-gray-600 mb-4">Interactive dashboard with real-time performance metrics</p>
            <button className="w-full bg-purple-600 text-white py-2 px-4 rounded-lg hover:bg-purple-700 transition-colors">
              Open Dashboard
            </button>
          </div>

          <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center mb-4">
              <FileText className="w-8 h-8 text-orange-600 mr-3" />
              <h3 className="text-xl font-semibold text-gray-800">Export Reports</h3>
            </div>
            <p className="text-gray-600 mb-4">Export performance data in various formats for external use</p>
            <button className="w-full bg-orange-600 text-white py-2 px-4 rounded-lg hover:bg-orange-700 transition-colors">
              Export Data
            </button>
          </div>
        </div>
      </div>
    );
  };

  // PriTools Content Component
  const PriToolsContent = ({ activeTab, setActiveTab }) => {
    return (
      <div className="space-y-6">
        {/* Exam Paper Generator Overlay */}
        {showExamPaperGenerator && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-xl font-semibold text-gray-800">Examination Paper Generator</h2>
                <button
                  onClick={() => setShowExamPaperGenerator(false)}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="overflow-auto max-h-[calc(90vh-80px)]">
                <ExamPaperGenerator />
              </div>
            </div>
          </div>
        )}

        {/* PriTools Sub-tab Navigation */}
        <div className="bg-white shadow-sm border-b">
          <div className="max-w-7xl mx-auto px-4">
            <nav className="flex space-x-8 overflow-x-auto">
              {[
                { id: 'generator', label: 'Generator', icon: FileText },
                { id: 'assessment', label: 'Assessment', icon: BarChart3 },
                { id: 'performance', label: 'Performance Report', icon: Settings }
              ].map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    <span>{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        {/* PriTools Content */}
        <div className="max-w-7xl mx-auto px-4 py-6">
          {activeTab === 'generator' && <GeneratorPage />}
          {activeTab === 'assessment' && <AssessmentPage />}
          {activeTab === 'performance' && <PerformanceReportPage />}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Sub-tab Navigation */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4">
          <nav className="flex space-x-8 overflow-x-auto">
            {[
              { id: 'p1', label: '小一', icon: BookOpen },
              { id: 'p2', label: '小二', icon: GraduationCap },
              { id: 'p3', label: '小三', icon: Users },
              { id: 'p4', label: '小四', icon: Target },
              { id: 'p5', label: '小五', icon: Award },
              { id: 'p6', label: '小六', icon: Brain },
              { id: 'pritools', label: 'PriTools', icon: Settings }
            ].map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id)}
                  className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${
                    activeSubTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Sub-tab Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {activeSubTab === 'pritools' ? (
          <PriToolsContent activeTab={activePriToolsTab} setActiveTab={setActivePriToolsTab} />
        ) : (
          renderPrimaryYearContent(primaryData[activeSubTab], loadingStates[activeSubTab])
        )}
      </div>

      {/* Drill Progress Modal */}
      {Object.values(drillProgress).some(progress => progress.isGenerating) && (
        <DrillProgress
          isGenerating={true}
          startTime={Object.values(drillProgress).find(p => p.isGenerating)?.startTime}
          tokensUsed={Object.values(drillProgress).find(p => p.isGenerating)?.tokensUsed}
          estimatedTokens={Object.values(drillProgress).find(p => p.isGenerating)?.estimatedTokens}
          costIncurred={Object.values(drillProgress).find(p => p.isGenerating)?.costIncurred}
          stage={Object.values(drillProgress).find(p => p.isGenerating)?.stage}
          message={Object.values(drillProgress).find(p => p.isGenerating)?.message}
        />
      )}
    </div>
  );
};

export default PrimaryMathsContainer; 